import { useEffect } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import { Button } from "@asosteam/asos-web-component-library-button";
import { NotificationBar } from "@asosteam/asos-web-component-library-notification-bar";
import { verificationCustomerStatus } from "../../constants/verification-status";
import { replaceJSX, isAuthenticated, promptLogin } from "../utilities";
import styles from "./styles";
import { CopyCodeButton } from "./copy-code";
import { useLocaleProvider, useTranslate } from "../../context/locale-provider";
import { ErrorPageContainer } from "../error";
import { trackPageEvent } from "../../utilities/trackPageEvent";

export default function ViewDiscount({
  sheerIdProgramId,
  studentCode,
  customerStatus,
  discountInternalError,
  sheerIdLocale,
}) {
  const determineTrackingStatusMessage = () => {
    if (localStorage.getItem("sv_post_success")) {
      localStorage.removeItem("sv_post_success");
      return "success";
    }
    if (customerStatus === verificationCustomerStatus.ERROR) {
      return "failure";
    }
    if (customerStatus === verificationCustomerStatus.PENDING_VERIFICATION) {
      return "pending";
    }
    return "";
  };

  useEffect(() => {
    const authStatus = async () => {
      const authResult = await isAuthenticated();

      if (!authResult) {
        await promptLogin();
      } else {
        trackPageEvent({
          content: {
            layout: "student-verification-page-load",
            pageName: "student verification final page",
            verificationStatus: determineTrackingStatusMessage(),
          },
        });
      }
    };

    authStatus();
  }, []);

  const translate = useTranslate(useLocaleProvider());

  if (discountInternalError) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <ErrorPageContainer
            errorType="internal"
            sheerIdProgramId={sheerIdProgramId}
            sheerIdLocale={sheerIdLocale}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <>
        <div className={styles.content}>
          <div>
            {customerStatus === verificationCustomerStatus.ACTIVE && (
              <>
                <>
                  <h1 className="app_barnsley5">
                    {translate("sv_success_header")}
                  </h1>
                  <p className="app_leavesden2">
                    {translate("sv_success_desc")}
                  </p>
                </>
                <div className={styles.discount}>
                  <span className="app_london1">{studentCode}</span>
                  <CopyCodeButton
                    value={studentCode}
                    title={translate("sv_success_copy_code")}
                    successMessage={translate("sv_success_code_copied")}
                  />
                </div>
              </>
            )}
            {customerStatus === verificationCustomerStatus.PENDING_DISCOUNT && (
              <>
                <>
                  <h1 className="app_barnsley5">
                    {translate("sv_code_not_ready_header")}
                  </h1>
                  <p className="app_leavesden2">
                    {translate("sv_code_not_ready_desc")}
                  </p>
                </>
                <div className={styles.discountPending}>
                  <a
                    className="app_london4"
                    href={`https://offers.sheerid.com/sheerid/help-center/?pid=${sheerIdProgramId}&locale=${sheerIdLocale}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {translate("sv_code_not_ready_help")}
                  </a>
                </div>
              </>
            )}
            {customerStatus === verificationCustomerStatus.ERROR && (
              <>
                <div className={styles.discountError}>
                  <h1 className="app_barnsley5">
                    {translate("sv_max_failure_heading")}
                  </h1>
                  <NotificationBar type="error" hasIcon>
                    {translate("sv_max_failure_error")}
                  </NotificationBar>
                  <p className="app_leavesden2">
                    {replaceJSX(
                      translate("sv_max_failure_desc"),
                      "%{sheerIdHelpCenterLink}",
                      <a
                        key="link"
                        href={`https://offers.sheerid.com/sheerid/help-center/?pid=${sheerIdProgramId}&locale=${sheerIdLocale}`}
                        className={styles.sheerIdCCLink}
                      >
                        {translate("sv_max_failure_desc_link_text")}
                      </a>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        <div className={styles.footer}>
          <h2 className="app_london1">{translate("sv_hub_heading")}</h2>
          <Button
            buttonType="secondary"
            className={classnames("app_button", styles.browseButton)}
            onClick={() => window.open("/discover/students-hub", "_blank")}
          >
            {translate("sv_hub_cta")}
          </Button>
        </div>
      </>
    </div>
  );
}

ViewDiscount.propTypes = {
  studentCode: PropTypes.string,
  customerStatus: PropTypes.string,
  sheerIdProgramId: PropTypes.string,
  discountInternalError: PropTypes.bool,
  sheerIdLocale: PropTypes.string.isRequired,
};

ViewDiscount.defaultProps = {
  studentCode: null,
  customerStatus: null,
  sheerIdProgramId: "",
  discountInternalError: false,
};
